import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Briefcase, ArrowRight } from "@phosphor-icons/react";
import { TaxShell, StatusBadge, cad, STATUS_META } from "@/components/tax/shared";

const REVIEW = { not_started: "Not started", in_review: "In review", waiting_on_client: "Waiting on client", ready_to_file: "Ready to file" };

export default function PreparerDashboard() {
  const nav = useNavigate();
  const [filter, setFilter] = useState("");
  const { data: cases = [], isLoading, error } = useQuery({ queryKey: ["preparer-cases"], queryFn: async () => (await api.get("/preparer/cases")).data, retry: false });
  const shown = filter ? cases.filter((c) => c.status === filter) : cases;
  const count = (s) => cases.filter((c) => c.status === s).length;

  return (
    <TaxShell testId="preparer-dashboard">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-600">Tax preparer</p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 mt-1 mb-2">Case dashboard</h1>
      <p className="text-sm text-slate-600 mb-6 max-w-2xl">Only returns customers explicitly authorized you to access appear here. Every view, SIN reveal, document download and export is logged.</p>
      {error && <p className="text-red-600" data-testid="preparer-access-error">You need an approved tax-preparer account to view this page.</p>}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {["sent_to_preparer", "action_required", "awaiting_authorization", "filed", "accepted"].map((s) => (
          <button key={s} data-testid={`preparer-filter-${s}`} onClick={() => setFilter(filter === s ? "" : s)}
            className={`tax-card p-4 text-left transition-colors duration-200 ${filter === s ? "border-red-500 ring-2 ring-red-500/20" : ""}`}>
            <p className="text-2xl font-black text-slate-900">{count(s)}</p>
            <p className="text-xs text-slate-500">{STATUS_META[s].label}</p>
          </button>
        ))}
      </div>
      <div className="tax-card overflow-x-auto" data-testid="preparer-cases-table">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr><th className="p-4">Customer</th><th className="p-4">Year</th><th className="p-4">Status</th><th className="p-4">Review</th><th className="p-4">Estimate</th><th className="p-4">Flags</th><th /></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td className="p-4 text-slate-500" colSpan={7}>Loading…</td></tr>}
            {!isLoading && shown.length === 0 && <tr><td className="p-6 text-slate-500" colSpan={7} data-testid="preparer-cases-empty"><Briefcase className="inline h-4 w-4 mr-1" /> No cases.</td></tr>}
            {shown.map((c) => (
              <tr key={c.id} onClick={() => nav(`/preparer/${c.id}`)} data-testid={`preparer-case-row-${c.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                <td className="p-4 font-semibold text-slate-900">{c.customer}</td>
                <td className="p-4">{c.tax_year}</td>
                <td className="p-4"><StatusBadge status={c.status} /></td>
                <td className="p-4 text-slate-600">{REVIEW[c.review_status] || "—"}</td>
                <td className="p-4">{c.estimated_balance > 0 ? <span className="text-red-600">owing {cad(c.estimated_balance)}</span> : <span className="text-emerald-700">refund {cad(c.estimated_refund)}</span>}</td>
                <td className="p-4">{c.review_flags > 0 ? <span className="text-amber-700 font-semibold">{c.review_flags} review</span> : "—"}</td>
                <td className="p-4"><ArrowRight className="h-4 w-4 text-slate-400" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TaxShell>
  );
}
